/* What this deployment can do with a given source family — read from the deployment, never listed.
 *
 * **The three capabilities are the API's own three fields**, `harvestable`, `executes_rules` and
 * `profiles_columns` from `GET /v1/catalog/connectors`. That is not a convenience: this file used to
 * be a hardcoded trio (postgresql, oracle, mysql), and the day Snowflake and Databricks became
 * harvestable every card for one said "has no driver in this build" beside a source this platform
 * can harvest. A frontend copy of a backend capability is a claim that goes stale in the direction
 * that makes the platform look worse than it is, and nothing fails when it does.
 *
 * **Three separate answers, never one "supported" flag.** Databricks is metadata-only here — it has
 * a metadata connector and no row source — so a card that collapsed the three would either promise
 * rule execution it cannot deliver or hide a working harvest. The reference design's tick-or-dot per
 * capability is the right shape for exactly that reason, and each one carries its own sentence.
 */

import type { Connection, ConnectorProfile } from '@/shared/api/generated/catalog';

/** The three capabilities, in the order a source is used: harvest it, then measure it. */
export const CAPABILITIES = ['harvest', 'rules', 'profiling'] as const;

export type Capability = (typeof CAPABILITIES)[number];

/** Whether the deployment offers a capability, or whether we could not tell.
 *
 * `unknown` is its own answer rather than folded into `no`, and it is the one that matters most: the
 * connector catalogue is a separate request that can fail on its own, and a failed read is not
 * evidence that a source cannot be harvested. Reporting `no` there would put a false "this cannot
 * run rules" on every source's page whenever one request 500s — a claim about the deployment made to
 * report our own failure, which is the class this codebase records at nine sites.
 */
export type Support = 'yes' | 'no' | 'unknown';

export type CapabilitySupport = Readonly<Record<Capability, Support>>;

const UNKNOWN: CapabilitySupport = {
  harvest: 'unknown',
  rules: 'unknown',
  profiling: 'unknown',
};

export function capabilitiesOf(
  connection: Connection,
  profiles: readonly ConnectorProfile[],
): CapabilitySupport {
  const profile = profiles.find(
    (entry) => entry.connector_type === connection.connector_type,
  );
  if (profile === undefined) return UNKNOWN;
  return {
    harvest: profile.harvestable ? 'yes' : 'no',
    /* Reported independently of `harvest` even though a source that cannot be harvested cannot
     * usefully run rules either. Deriving one from the other would make the card assert a
     * dependency the API does not state, and the API is the authority — a family could in principle
     * gain a row source before a metadata connector, and the card should then say so rather than
     * hide it behind an inference. */
    rules: profile.executes_rules ? 'yes' : 'no',
    profiling: profile.profiles_columns ? 'yes' : 'no',
  };
}

/* ------------------------------------------------------------------ the coarse verdict

 * The three-way reach label the list and the register form already use. Kept because a summary line
 * is a different question from the per-capability readout — "can I use this source at all" against
 * "which of the three things works" — and a screen that only had the second would make a reader
 * derive the first.
 */
export type SourceReach = 'full' | 'metadataOnly' | 'unavailable' | 'unknown';

export function reachOf(support: CapabilitySupport): SourceReach {
  if (support.harvest === 'unknown') return 'unknown';
  if (support.harvest === 'no') return 'unavailable';
  return support.rules === 'yes' ? 'full' : 'metadataOnly';
}
